import { createSlice } from '@reduxjs/toolkit';

import { SLICER_KEYS } from 'types/services';
import { setDrawer } from './reducers/setDrawer';
import { resetFilters, setFilters, setSavedFilters, setFiltersToApply, setDefaultFiltersOnlyIfNoFilterApplied } from './reducers/setFilters';
import type { DrawerContentType } from 'components/mui/common/Drawer/DrawerWrapper';
import type { FilterValue } from 'types/userFilters';

export enum DrawerContent {
  FILTER_DRAWER = 'filter',
}

export type FilterOption = {
  label: string;
  value: string;
};

export type DateFilterOption = {
  fromDate?: string;
  toDate?: string;
};

export type NumberRangeFilterOption = {
  fromNumber?: number;
  toNumber?: number;
};

export const isFilterOptionArray = (value: AppliedFilter['value']): value is FilterOption[] => {
  return Array.isArray(value) && value.length > 0 && typeof value[0] === 'object' && 'label' in value[0] && 'value' in value[0];
};

export const isDateFilterOption = (value: unknown): value is DateFilterOption => {
  return value !== null && typeof value === 'object' && ('fromDate' in value || 'toDate' in value);
};

export const isNumberRangeFilterOption = (value: unknown): value is NumberRangeFilterOption => {
  return value !== null && typeof value === 'object' && ('fromNumber' in value || 'toNumber' in value);
};

export const isTableValueArray = (value: unknown): value is Array<{ value: string }> => {
  return Array.isArray(value) && value.length > 0 && typeof value[0] === 'object' && 'value' in value[0];
};

// TODO: This is a bad type design, there should be a filter type per value type and AppliedFilter should be a union of those types - for future refactors
export interface AppliedFilter {
  formItemId: string;
  formItemType: string;
  value?: string | string[] | FilterOption[] | DateFilterOption | NumberRangeFilterOption;
}

interface SavedFilters {
  userFilterId: string;
  name: string;
  isDefault: boolean;
  isGlobal?: boolean;
  values: FilterValue[];
}

export interface DrawerState {
  isOpen: boolean;
  contentType: DrawerContentType | null;
  dataSchema: unknown;
  agendaId?: string;
  viewId?: string;
  tableId?: string;
  canSave?: boolean;
  appliedFilters?: Record<string, AppliedFilter[] | undefined>;
  filtersToApply?: Record<string, AppliedFilter[]>;
  savedFilters?: Record<string, SavedFilters[]>;
  referencedFormItemsMap?: Record<string, string>;
  entity: string;
  type?: 'userAgenda' | 'table';
}

export const initialState: DrawerState = {
  isOpen: false,
  contentType: null,
  appliedFilters: {},
  filtersToApply: {},
  savedFilters: {},
  dataSchema: {},
  entity: '',
  type: 'userAgenda',
};

export const drawerState = createSlice({
  name: SLICER_KEYS.DRAWER_STATE,
  initialState,
  reducers: {
    setDrawer,
    setFilters,
    setFiltersToApply,
    setDefaultFiltersOnlyIfNoFilterApplied,
    setSavedFilters,
    resetFilters,
  },
});

export const drawerStateActions = drawerState.actions;
